var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormControl, FormHelperText, Rating, Box, Button, Typography, Tooltip, } from "@mui/material";
import { RadioButtonUncheckedRounded } from "@mui/icons-material";
import { buildFieldValidation } from "../../validators/field-components";
import CommentArea from "../common/CommentArea";
import SurveyJSIcon from "../../utils/SurveyJSIcon";
import { useElementConditions } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getCommentFieldName, getInitialValueForElement, } from "../../../../utils/forms-functions";
import { getElementValueKey } from "../../../../utils/helpers";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
var SMILEYS = [
    {
        value: 1,
        icon: "smiley-rate1-24x24",
        label: "Extremamente Insatisfeito",
        color: "#e53935",
    },
    {
        value: 2,
        icon: "smiley-rate2-24x24",
        label: "Muito Insatisfeito",
        color: "#f44336",
    },
    {
        value: 3,
        icon: "smiley-rate3-24x24",
        label: "Bastante Insatisfeito",
        color: "#fb8c00",
    },
    {
        value: 4,
        icon: "smiley-rate4-24x24",
        label: "Insatisfeito",
        color: "#ffb300",
    },
    {
        value: 5,
        icon: "smiley-rate5-24x24",
        label: "Levemente Insatisfeito",
        color: "#ffd600",
    },
    {
        value: 6,
        icon: "smiley-rate6-24x24",
        label: "Neutro",
        color: "#fff176",
    },
    {
        value: 7,
        icon: "smiley-rate7-24x24",
        label: "Levemente Satisfeito",
        color: "#aed581",
    },
    {
        value: 8,
        icon: "smiley-rate8-24x24",
        label: "Satisfeito",
        color: "#4caf50",
    },
    {
        value: 9,
        icon: "smiley-rate9-24x24",
        label: "Muito Satisfeito",
        color: "#2196f3",
    },
    {
        value: 10,
        icon: "smiley-rate10-24x24",
        label: "Extremamente Satisfeito",
        color: "#1565c0",
    },
];
var SMILEYS_ICON_INDICES = {
    2: [3, 7],
    3: [3, 5, 7],
    4: [3, 5, 6, 7],
    5: [3, 4, 5, 6, 7],
    6: [3, 4, 5, 6, 7, 8],
    7: [2, 3, 4, 5, 6, 7, 8],
    8: [2, 3, 4, 5, 6, 7, 8, 9],
    9: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    10: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
};
var StarsRatingWithTooltip = function (_a) {
    var values = _a.values, displayValue = _a.displayValue, isEnabled = _a.isEnabled, onChangeStar = _a.onChangeStar;
    var _b = React.useState(-1), hover = _b[0], setHover = _b[1];
    var activePosition = hover !== -1 ? hover : displayValue;
    var activeEntry = values[activePosition - 1];
    var tooltipTitle = activeEntry ? activeEntry.text : "";
    return (_jsx(Tooltip, __assign({ title: tooltipTitle, arrow: true, placement: "bottom" }, { children: _jsx(Rating, { value: displayValue, max: values.length, getLabelText: function (starPosition) {
                var entry = values[starPosition - 1];
                return entry ? entry.text : starPosition.toString();
            }, onChange: function (_, newValue) { return onChangeStar(newValue); }, onChangeActive: function (_, newHover) { return setHover(newHover); }, disabled: !isEnabled, sx: { fontSize: window.innerWidth > 450 ? "2.5rem" : "1.8rem" } }) })));
};
var RatingField = function (_a) {
    var _b, _c;
    var element = _a.element, errors = _a.errors;
    var unregister = useFormContext().unregister;
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _d = useElementConditions(element), isVisible = _d.isVisible, isEnabled = _d.isEnabled, isInteractive = _d.isInteractive, fieldRequired = _d.isRequired;
    var validation = useMemo(function () {
        return buildFieldValidation(element, {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
        });
    }, [element, fieldRequired, isEnabled]);
    var valueKeys = useMemo(function () {
        return element.showCommentArea
            ? [valueKey, getCommentFieldName(valueKey)]
            : [valueKey];
    }, [valueKey, element.showCommentArea]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var initialValue = getInitialValueForElement(element);
    var rateType = element.rateType || "";
    var rateMin = (_b = element.rateMin) !== null && _b !== void 0 ? _b : 1;
    var rateMax = (_c = element.rateMax) !== null && _c !== void 0 ? _c : 5;
    var rateStep = element.rateStep || 1;
    var displayRateDescriptionsAsExtremeItems = element.displayRateDescriptionsAsExtremeItems && rateType === "";
    var rateDescriptionLocation = element.rateDescriptionLocation || "leftRight";
    var generateDefaultRatingValues = useCallback(function () {
        var result = [];
        if (element.rateValues && Array.isArray(element.rateValues)) {
            for (var _i = 0, _a = element.rateValues; _i < _a.length; _i++) {
                var value = _a[_i];
                if (typeof value === "number") {
                    result.push({ value: Number(value), text: value.toString() });
                }
                else if (typeof value === "string") {
                    result.push({ value: value, text: value });
                }
                else {
                    result.push(value);
                }
            }
        }
        else {
            var current = rateMin;
            while (current <= rateMax) {
                result.push({ value: current, text: current.toString() });
                current += rateStep;
            }
        }
        return result;
    }, [element, rateMin, rateMax, rateStep]);
    var renderDefaultRating = useMemo(function () { return function (field) {
        var defaultRatingValues = generateDefaultRatingValues();
        return (_jsx(Box, __assign({ sx: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
            } }, { children: defaultRatingValues.map(function (value, index) {
                var isSelected = field.value === value.value;
                var isFirst = index === 0;
                var isLast = index === defaultRatingValues.length - 1;
                var showAsText = displayRateDescriptionsAsExtremeItems &&
                    ((isFirst && element.minRateDescription) ||
                        (isLast && element.maxRateDescription));
                return (_jsx(Button, __assign({ variant: isSelected ? "contained" : "outlined", onClick: function () { return field.onChange(value.value); }, disabled: !isInteractive, sx: {
                        minWidth: showAsText ? "auto" : 48,
                        height: 48,
                        fontSize: window.innerWidth > 450 ? "1.2rem" : "1rem",
                        fontWeight: isSelected ? 600 : 400,
                        px: showAsText ? 2 : 1,
                    } }, { children: showAsText
                        ? isFirst
                            ? element.minRateDescription
                            : element.maxRateDescription
                        : value.text }), value.value));
            }) })));
    }; }, [
        displayRateDescriptionsAsExtremeItems,
        generateDefaultRatingValues,
        isInteractive,
        element.minRateDescription,
        element.maxRateDescription,
    ]);
    var renderStarsRating = useMemo(function () { return function (field) {
        var starsRatingValues = generateDefaultRatingValues();
        var selectedIndex = starsRatingValues.findIndex(function (v) { return v.value === field.value; });
        var displayValue = selectedIndex >= 0 ? selectedIndex + 1 : 0;
        return (_jsx(StarsRatingWithTooltip, { values: starsRatingValues, displayValue: displayValue, isEnabled: isInteractive, onChangeStar: function (newValue) {
                if (newValue && newValue > 0) {
                    field.onChange(starsRatingValues[newValue - 1].value);
                }
                else {
                    field.onChange(null);
                }
            } }));
    }; }, [generateDefaultRatingValues, isInteractive]);
    var renderSmileysRating = useMemo(function () { return function (field) {
        var smileysValues = generateDefaultRatingValues();
        return (_jsx(Box, __assign({ sx: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
            } }, { children: smileysValues.map(function (entry, index) {
                var isSelected = field.value === entry.value;
                var iconIndices = SMILEYS_ICON_INDICES[smileysValues.length];
                var smileyIndex = iconIndices === null || iconIndices === void 0 ? void 0 : iconIndices[index];
                var smiley = SMILEYS[smileyIndex];
                return (_jsx(Box, __assign({ sx: {
                        cursor: !isInteractive ? "default" : "pointer",
                        opacity: !isInteractive && !isSelected ? 0.5 : 1,
                        transform: isSelected ? "scale(1.1)" : "scale(1)",
                        transition: "all 0.2s",
                    }, onClick: function () { return isInteractive && field.onChange(entry.value); } }, { children: _jsx(Tooltip, __assign({ title: element.rateValues
                            ? entry.text
                            : (smiley === null || smiley === void 0 ? void 0 : smiley.label) || entry.text, arrow: true }, { children: _jsx("span", { children: !!smiley ? (_jsx(SurveyJSIcon, { iconNameKey: smiley.icon, isSelected: isSelected, selectedColor: smiley.color })) : (_jsx(Box, __assign({ component: "div", sx: { width: 54, height: 54 } }, { children: _jsx(RadioButtonUncheckedRounded, { sx: {
                                        fontSize: 56,
                                        width: "100%",
                                        height: "100%",
                                        color: isSelected
                                            ? "primary.main"
                                            : "action.disabled",
                                        transition: "all 0.2s",
                                    } }) }))) }) })) }), entry.value));
            }) })));
    }; }, [generateDefaultRatingValues, isInteractive, element]);
    var showMin = !!element.minRateDescription && !displayRateDescriptionsAsExtremeItems;
    var showMax = !!element.maxRateDescription && !displayRateDescriptionsAsExtremeItems;
    var isLeftRight = rateDescriptionLocation === "leftRight";
    var RenderDescriptionRow = function (_a) {
        var reverse = _a.reverse;
        if (!showMin && !showMax)
            return null;
        return (_jsxs(Box, __assign({ sx: {
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
            } }, { children: [(showMin && reverse
                    ? !rateDescriptionLocation.includes("top")
                    : rateDescriptionLocation.includes("top")) && (_jsx(Typography, __assign({ variant: "body2", color: "text.secondary" }, { children: element.minRateDescription }))), (showMax && reverse
                    ? !(rateDescriptionLocation === "top")
                    : rateDescriptionLocation === "top") && (_jsx(Typography, __assign({ variant: "body2", color: "text.secondary", sx: { ml: "auto" } }, { children: element.maxRateDescription })))] })));
    };
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ fullWidth: true, error: !!error, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, defaultValue: initialValue, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var field = _a.field;
                            var ratingComponent;
                            switch (rateType) {
                                case "stars":
                                    ratingComponent = renderStarsRating(field);
                                    break;
                                case "smileys":
                                    ratingComponent = renderSmileysRating(field);
                                    break;
                                default:
                                    ratingComponent = renderDefaultRating(field);
                                    break;
                            }
                            return (_jsxs(Box, { children: [_jsxs(Box, __assign({ sx: {
                                            display: isLeftRight ? "flex" : "inline-flex",
                                            flexDirection: isLeftRight ? "row" : "column",
                                            justifyContent: "start",
                                            alignItems: isLeftRight ? "center" : "stretch",
                                            gap: isLeftRight ? 2 : 1,
                                            width: isLeftRight ? undefined : "fit-content",
                                        } }, { children: [isLeftRight && showMin && (_jsx(Typography, __assign({ variant: "body2", color: "text.secondary", sx: {
                                                    minWidth: "fit-content",
                                                } }, { children: element.minRateDescription }))), !isLeftRight && _jsx(RenderDescriptionRow, { reverse: false }), ratingComponent, !isLeftRight && _jsx(RenderDescriptionRow, { reverse: true }), isLeftRight && showMax && (_jsx(Typography, __assign({ variant: "body2", color: "text.secondary", sx: { minWidth: "fit-content" } }, { children: element.maxRateDescription })))] })), error && (_jsx(FormHelperText, __assign({ error: true, sx: { mt: 1 } }, { children: error.message }))), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] }));
                        } })] })) }))) : null }));
};
export default RatingField;
